// Архитектура голосов:
// — «Спутник» = AI-интерфейс на интейке (онбординг), знакомится и передаёт
//   пользователя конкретному живому куратору.
// — CURATOR_PERSONAS — именованные редакторы, среди которых Спутник выбирает
//   одного под стиль пользователя. В демо их три: Марк, Юля, Лев.
// — ASSIGNED_CURATOR — обратно-совместимый алиас на Марка для старых вызовов.

export type CuratorId = "mark" | "yulia" | "lev";

export interface CuratorPersona {
  firstName: string;
  initial: string;
  /** Подпись в чат-пузырях (CuratorBubble). */
  shortSig: string;
  /** Подпись в конце писем. */
  letterSig: string;
  /** Роль под подписью письма. */
  letterRole: string;
  /** Как обращаться в «редакции» — дательный падеж. */
  footerLabel: string;
  /** Дательный с предлогом «с» — для «перейти в чат с …». */
  withLabel: string;
  /** Краткое описание для экрана назначения. */
  bio: string;
  /** Развёрнутое описание для экрана назначения. */
  bioDetail: string;
  /** Сильные стороны — почему подходит пользователю. */
  strengths: string[];
  /** Telegram-хендл для мини-чата советов. */
  username: string;
}

export const CURATOR_PERSONAS: Record<CuratorId, CuratorPersona> = {
  mark: {
    firstName: "Марк",
    initial: "Г.",
    shortSig: "Марк, редактор",
    letterSig: "Искренне ваш, Марк Г.",
    letterRole: "Редактор · Спутник",
    footerLabel: "Марку",
    withLabel: "с Марком",
    bio: "Двенадцатый год в редакции. Читает анкеты как прозу.",
    bioDetail:
      "Марк — редактор старой школы: медленный, внимательный, не любит лишних слов. Ему нравятся читатели, которым важна тишина между строк.",
    strengths: [
      "Слышит тон, а не громкость",
      "Помнит детали из первого письма",
      "Никогда не торопит с ответом",
    ],
    username: "mark_kurator",
  },
  yulia: {
    firstName: "Юля",
    initial: "К.",
    shortSig: "Юля, редактор",
    letterSig: "Искренне ваша, Юля К.",
    letterRole: "Редактор · Спутник",
    footerLabel: "Юле",
    withLabel: "с Юлей",
    bio: "Редактор с восемнадцатилетним стажем. Пишет длинные письма, читает между строк.",
    bioDetail:
      "Юля — редактор, выросший на толстых журналах. Её тексты медленные, чувственные; она умеет услышать то, что люди не решаются сказать напрямую. Подходит тем, для кого важны нюанс и дыхание фразы.",
    strengths: [
      "Читает эмоциональный подтекст",
      "Пишет длинно и внимательно",
      "Не торопит чужую уязвимость",
    ],
    username: "yulia_kurator",
  },
  lev: {
    firstName: "Лев",
    initial: "П.",
    shortSig: "Лев, редактор",
    letterSig: "Искренне ваш, Лев П.",
    letterRole: "Редактор · Спутник",
    footerLabel: "Льву",
    withLabel: "со Львом",
    bio: "Одиннадцатый год в редакции. Ссылается на Мураками, чаще чем сам замечает.",
    bioDetail:
      "Лев — редактор с иронией и тихим взглядом. Не выдаёт чувств, но замечает книги на полке и фильмы, которые смотрел собеседник. Подходит тем, для кого важна интеллектуальная химия и культурные совпадения.",
    strengths: [
      "Слышит культурный контекст",
      "Уважает тишину и подтекст",
      "Задаёт точные короткие вопросы",
    ],
    username: "lev_kurator",
  },
};

/** Возвращает персону по id; если id неизвестен — фолбэк на Марка. */
export function getCurator(id: string | null | undefined): CuratorPersona {
  if (id === "mark" || id === "yulia" || id === "lev") {
    return CURATOR_PERSONAS[id];
  }
  return CURATOR_PERSONAS.mark;
}

/**
 * Обратно-совместимый алиас. Новый код должен использовать `useCurator()`.
 * @deprecated use `useCurator()` to read the assigned curator dynamically.
 */
export const ASSIGNED_CURATOR: CuratorPersona = CURATOR_PERSONAS.mark;

/** Подпись от имени «Спутника» — AI-интерфейса на этапе онбординга. */
export const SPUTNIK_SIG = "Спутник";
